/**
 * #1876 Feature-usage beacon — the Akira LIMS pattern, ported. Answers "is
 * anyone using this feature", which the #919 once-per-session device beacon
 * cannot. Fail-silent by the same contract: a lost beacon must never affect
 * the app, so every path swallows.
 *
 * What's emitted: page_load on route change (tab = first path segment) and
 * api_read / api_write / api_error from the api.ts wrappers. Endpoints are
 * recorded path-only — query strings can carry material and product names and
 * are stripped before anything leaves the browser (the server drops any that
 * slip through).
 */

export interface UsageEvent {
  eventType: string;
  tab?: string | null;
  endpoint?: string | null;
  durationMs?: number | null;
  resultCount?: number | null;
  meta?: Record<string, string | number | boolean | null>;
}

const ENDPOINT = "/api/telemetry/events";
const FLUSH_DELAY_MS = 2_000;
const MAX_QUEUE = 20;

let activeTab: string | null = null;
let queue: UsageEvent[] = [];
let flushTimer: number | undefined;
let installed = false;

// #1928: test runtimes (vitest jsdom/workerd) define window+navigator, so
// events queue up and the deferred flush fires at teardown — where a
// relative-URL fetch rejects UNHANDLED and turns an all-green run into
// exit 1. Same side-channel class as the postgres teardown leak: gate the
// emitter on an explicit environment signal, not on API presence.
const IS_TEST_RUNTIME = typeof import.meta !== "undefined" && import.meta.env?.MODE === "test";

/**
 * True only in a real browser document: window AND document exist, the
 * runtime is not a test pool, and the beacon endpoint resolves to an absolute
 * http(s) URL against the page origin. Enqueue, flush and install all read
 * this, so a test runtime never even schedules the deferred flush that leaked
 * at teardown.
 */
function isBrowserRuntime(): boolean {
  if (IS_TEST_RUNTIME) return false;
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (typeof navigator === "undefined") return false;
  return beaconUrl() !== null;
}

function beaconUrl(): URL | null {
  try {
    const url = new URL(ENDPOINT, window.location.origin);
    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

/** Never throws, never leaves a rejection behind. */
function flush(): void {
  flushTimer = undefined;
  if (!queue.length) return;
  if (!isBrowserRuntime()) {
    queue = [];
    return;
  }
  const events = queue;
  queue = [];
  const body = JSON.stringify({ events });
  try {
    const url = beaconUrl();
    if (!url) return;
    // Absolute URL for sendBeacon too: workerd exposes navigator.sendBeacon
    // and rejects a relative URL in a detached promise (the #1928 leak).
    if (navigator.sendBeacon?.(url.href, new Blob([body], { type: "application/json" }))) return;
    // Promise.resolve() guards a stubbed fetch that returns a non-promise;
    // the two-arg then() means no rejection can ever escape this call.
    void Promise.resolve(
      fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
      }),
    ).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    // Telemetry must never interrupt the application.
  }
}

export function logUsageEvent(event: UsageEvent): void {
  if (!isBrowserRuntime()) return;
  queue.push({ ...event, tab: event.tab ?? activeTab });
  if (queue.length >= MAX_QUEUE) return flush();
  if (flushTimer === undefined) flushTimer = window.setTimeout(flush, FLUSH_DELAY_MS);
}

/** The tab a path is counted under: its first segment ("/lab/x" is "lab"), "home" for "/". */
export function pageTab(pathname: string): string {
  return pathname.split("/").filter(Boolean)[0] ?? "home";
}

/** Route change → page_load, under pageTab(pathname). */
export function recordPageView(pathname: string): void {
  const tab = pageTab(pathname);
  activeTab = tab;
  logUsageEvent({ eventType: "page_load", tab });
}

/** Path only — never the query string. Collapses ids so endpoints aggregate. */
export function endpointPath(url: string): string {
  let pathname: string;
  try {
    pathname = new URL(url, typeof window === "undefined" ? "http://localhost" : window.location.origin).pathname;
  } catch {
    pathname = url.split("?")[0];
  }
  return pathname
    .split("/")
    .map((seg) => (/^\d+$/.test(seg) || /^[0-9a-f-]{16,}$/i.test(seg) ? ":id" : seg))
    .join("/");
}

/** Best-effort row count from common list-shaped responses. */
export function resultCount(value: unknown): number | null {
  if (Array.isArray(value)) return value.length;
  if (!value || typeof value !== "object") return null;
  for (const key of ["results", "items", "rows", "data", "batches", "products"]) {
    const item = (value as Record<string, unknown>)[key];
    if (Array.isArray(item)) return item.length;
  }
  return null;
}

/** Flush on pagehide so tail-of-session events aren't lost. Idempotent. */
export function installUsageTelemetry(): () => void {
  if (typeof window === "undefined" || installed) return () => undefined;
  installed = true;
  const onPageHide = () => flush();
  window.addEventListener("pagehide", onPageHide);
  return () => {
    window.removeEventListener("pagehide", onPageHide);
    installed = false;
    flush();
  };
}
